import { Injectable, inject } from '@angular/core';
import { Subscription, Subject, BehaviorSubject } from 'rxjs';
import { NonNullableFormBuilder } from '@angular/forms';
import { Pagination } from '@app/core/model/pagination.model';
import { BroadcastService, BroadcastMessage } from '@app/core/service';
import { SlideScrollService } from '@app/shell';
import { Badge } from '@app/achievements/model/badge.model';
import { AchievementsHttpService } from '@app/achievements/service/http/http.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { ContentHttpService } from '@app/content';
import { ItemsType, items } from '@app/achievements/components/menu/config';

@Injectable()
export class AchievementsListService {
    private readonly achievementsHttpService = inject(AchievementsHttpService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly slideScrollService = inject(SlideScrollService);
    private readonly contentService = inject(ContentHttpService);
    private readonly fb = inject(NonNullableFormBuilder);

    loading = new BehaviorSubject(false);
    accessableMissionsIds: string[] = [];

    _badges: Record<string, Record<number, Badge[] | null>> = {};

    _total: Record<string, number | null> = {};

    readonly form = this.fb.group({
        type: this.fb.control<ItemsType>('all'),
    });

    readonly type = this.form.controls.type;

    types = items;
    _pagination: Pagination = {
        offset: 0,
        limit: 12,
    };

    viewedPart: Badge[] | null = null;
    hasFetchedAll = false;

    private updatesEvent = new Subject();
    private subscriptions = new Subscription();

    init(): void {
        this.subscriptions.add(
            this.type.valueChanges
                .pipe(
                    tap(() => {
                        this.viewedPart = null;
                        this.updatesEvent.next(true);
                        this.slideScrollService.update$.next(true);
                    }),
                )
                .subscribe(),
        );
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Badges
     */
    get badges() {
        // create empty tree
        this.createEmptyTree();
        // check if page already loaded
        if (this._badges[this.type.value][this.pagination.offset]) {
            this.viewedPart = this._badges[this.type.value][this.pagination.offset];
        } else {
            this.loadList();
        }
        // return loaded page
        return this.viewedPart;
    }

    getBadgesAllLoaded() {
        // create empty tree
        this.createEmptyTree();
        // check if page already loaded
        if (this._badges[this.type.value][this.pagination.offset]) {
            const paginationLoadedOffsets: string[] = Object.keys(this._badges[this.type.value]) || [];
            const currentOffsetNumber = paginationLoadedOffsets.indexOf(`${this.pagination.offset}`) ?? 0;

            this.viewedPart = [];

            paginationLoadedOffsets.slice(0, currentOffsetNumber + 1).forEach((offset) => {
                this.viewedPart!.push(...(<Badge[]>this._badges[this.type.value][parseInt(offset)]));
            });
        } else {
            this.loadList();
        }

        return this.viewedPart;
    }

    /**
     * Get Total
     */
    get total() {
        return this._total[this.type.value] ? this._total[this.type.value] : 0;
    }

    /**
     * Set Pagination
     */
    set pagination(pagination: Pagination) {
        this._pagination = pagination;
    }

    /**
     * Get Pagination
     */
    get pagination() {
        return this._pagination;
    }

    nextPage() {
        const nextPagePagination: Pagination = {
            limit: this.pagination.limit,
            offset: this.pagination.offset + this.pagination.limit,
        };

        if (nextPagePagination.offset >= <number>this.total) {
            return;
        }

        this.pagination = nextPagePagination;
    }

    /**
     * Load Leaderboard
     */
    loadList(): void {
        if (this.loading.value) {
            return;
        }

        const type = this.type.value;
        const offset = this.pagination.offset;

        this.loading.next(true);
        this.updatesEvent.next(true);

        this.subscriptions.add(
            this.achievementsHttpService
                .getBadges('all', this.type.value, this.pagination.limit, this.pagination.offset)
                .pipe(
                    switchMap((badgesData) => {
                        const missionsIds = badgesData.badges.filter((badge) => badge.type !== 'activity').map((badge) => badge.missionId);

                        return this.contentService.checkIfExists(missionsIds).pipe(
                            tap((existingBadgesIds) => {
                                if (existingBadgesIds !== null && existingBadgesIds?.length) {
                                    this.accessableMissionsIds = this.accessableMissionsIds.concat(existingBadgesIds);
                                }
                            }),
                            map(() => badgesData),
                        );
                    }),
                )
                .subscribe((data) => this.handleResponse(data.badges, data.total, type, offset)),
        );
    }

    /**
     * Response Handler
     */
    private handleResponse(badges: Badge[], total: number, type: string, offset: number): void {
        this._badges[type][offset] = badges;
        this._total[type] = total;
        this.loading.next(false);
        this.updatesEvent.next(true);
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        // set object key
        if (!this._badges[this.type.value]) {
            this._badges[this.type.value] = {};
            this._total[this.type.value] = null;
        }
        // pagination
        if (!this._badges[this.type.value][this.pagination.offset]) {
            this._badges[this.type.value][this.pagination.offset] = null;
        }
    }

    /**
     * Reset Pagination and Form
     */
    private reset(): void {
        this._badges = {};
        this._total = {};
        this.viewedPart = null;
        this._pagination.offset = 0;
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'discussion-topic-created':
            case 'discussion-topic-reply-created':
            case 'discussion-reply-reply-created':
            case 'quiz-finished':
            case 'exam-finished':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
